import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CardHeader from "@material-ui/core/CardHeader";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import SearchIcon from "@material-ui/icons/Search";
import { Skeleton } from "@material-ui/lab";
import VirtualizedList from "components/List/VirtualizedList";
import UtilsFncs from "utils/UtilsFncs";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    justifyContent: "space-evenly",
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    height: 300,
    minHeight: 200,
    maxHeight: 300,
    backgroundColor: theme.palette.background.paper,
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
  listItem: {
    display: "flex",
    "& > .MuiListItemText-root > span": {
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      wordBreak: "keep-all",
      overflow: "hidden",
    },
  },
}));

const not = (a, b) => {
  return a.filter((value) => b.indexOf(value) === -1);
};

const intersection = (a, b) => {
  return a.filter((value) => b.indexOf(value) !== -1);
};

const union = (a, b) => {
  return [...a, ...not(b, a)];
};

const getItemLabelFromArray = (item, labelArray) => {
  let label = "";

  labelArray.some((labelToCheck) => {
    label = item[labelToCheck];

    return label;
  });

  return label;
};

const LoadingItem = () => (
  <ListItem style={{ padding: ".5rem" }}>
    <Skeleton />
  </ListItem>
);

const ItemsFilter = ({ label, loading, filteringItems, verifyItems }) => {
  const isMobile = IsMobile();

  const [filterValue, setFilterValue] = useState("");

  const handleFilterChange = (e) => {
    e.preventDefault();

    setFilterValue(e.target.value);
  };

  const handleVerify = useCallback(() => {
    verifyItems(filterValue);
  }, [filterValue, verifyItems]);

  const onKeyPress = useCallback(
    (e) => {
      if (e.code === "Enter") {
        handleVerify();
      }
    },
    [handleVerify]
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      width={isMobile ? "100%" : "80%"}
      marginLeft={isMobile ? "1rem" : "0rem"}
    >
      <TextField
        margin="dense"
        label={`Filtrar ${label}`}
        type="text"
        fullWidth
        variant="outlined"
        disabled={loading || filteringItems}
        value={filterValue}
        style={{ marginBottom: 10 }}
        onChange={handleFilterChange}
        inputProps={{
          onKeyPress,
        }}
      />
      <IconButtonLoader
        title="Filtrar opções"
        onClick={handleVerify}
        extraStyle={{ marginLeft: 10 }}
        icon={<SearchIcon color="primary" style={{ width: 18, height: 18 }} />}
        size="small"
        disabled={loading || filteringItems}
      />
    </Box>
  );
};

const ItemsList = ({
  label,
  initialItems,
  side,
  loading,
  onDrop,
  handleToggleAll,
  itemLabelKey,
  itemExtraLabelKey,
  itemTooltipKey,
  checked,
  setChecked,
  numberOfChecked,
}) => {
  const classes = useStyles();
  const [items, setItems] = useState(initialItems || null);
  const [filteringItems, setFilteringItems] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verifyItems = useCallback(
    (filterValue) => {
      setFilteringItems(true);

      return new Promise((resolve) => {
        const valueToCheck = filterValue.toLowerCase();

        if (valueToCheck === "") {
          setItems(initialItems);
        } else {
          const newItemsValue = initialItems.filter(
            (item) =>
              item[itemLabelKey]?.toLowerCase().includes(valueToCheck) ||
              item[itemExtraLabelKey]?.toLowerCase().includes(valueToCheck)
          );

          setItems(newItemsValue);
        }

        resolve(setFilteringItems(false));
      });
    },
    [initialItems, itemExtraLabelKey, itemLabelKey]
  );

  useEffect(() => {
    if (initialItems && !UtilsFncs.ArrayEquals(items, initialItems)) {
      setItems(initialItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems]);

  const handleToggle = useCallback(
    (value) => () => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
    },
    [checked, setChecked]
  );

  const onDragStart = (event, item) => {
    event.dataTransfer.setData("item", JSON.stringify(item));
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <div
        onDragOver={(event) => onDragOver(event)}
        onDrop={(event) => {
          onDrop(event, side);
        }}
      >
        <Grid container>
          {!loading && items && (
            <CardHeader
              className={classes.cardHeader}
              avatar={
                <Checkbox
                  onClick={handleToggleAll(items)}
                  checked={
                    numberOfChecked(items) === items.length &&
                    items.length !== 0
                  }
                  indeterminate={
                    numberOfChecked(items) !== items.length &&
                    numberOfChecked(items) !== 0
                  }
                  disabled={items.length === 0}
                  inputProps={{ "aria-label": "todos os itens selecionados" }}
                  color="primary"
                />
              }
              title={label}
              subheader={`${numberOfChecked(items)}/${
                items.length
              } selecionados`}
            />
          )}
          {!loading && (
            <ItemsFilter
              label={label}
              filteringItems={filteringItems}
              loading={loading}
              verifyItems={verifyItems}
            />
          )}
        </Grid>
        {!loading && <Divider />}
        <VirtualizedList className={classes.list}>
          {!loading && items ? (
            items?.map((value, index) => {
              const labelId = `transfer-list-all-item-${index}-label`;
              let text = "";
              let tooltipText = "";

              if (itemTooltipKey) {
                tooltipText = value[itemTooltipKey];
              }

              if (Array.isArray(itemLabelKey)) {
                text = getItemLabelFromArray(value, itemLabelKey);
              } else {
                text = value[itemLabelKey];
              }

              const primaryText = text || "";

              let primaryTextTooltip = "";

              if (itemTooltipKey) {
                primaryTextTooltip =
                  itemTooltipKey && value[itemExtraLabelKey]
                    ? `${tooltipText}   ${value[itemExtraLabelKey]}`
                    : tooltipText || "";
              } else {
                primaryTextTooltip =
                  itemExtraLabelKey && value[itemExtraLabelKey]
                    ? `${text}   ${value[itemExtraLabelKey]}`
                    : text || "";
              }

              return (
                <ListItem
                  key={index}
                  role="listitem"
                  onClick={handleToggle(value)}
                  draggable
                  className={classes.listItem}
                  onDragStart={(event) => onDragStart(event, value)}
                >
                  <DragIndicatorIcon />
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      inputProps={{ "aria-labelledby": labelId }}
                      color="primary"
                    />
                  </ListItemIcon>
                  <Tooltip title={primaryTextTooltip}>
                    <ListItemText id={labelId} primary={primaryText} />
                  </Tooltip>
                </ListItem>
              );
            })
          ) : (
            <>
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
            </>
          )}
        </VirtualizedList>
      </div>
    </>
  );
};

export default function TransferList({
  initialLeft,
  leftLabel,
  initialRight,
  rightLabel,
  loading,
  onLeftChange,
  onRightChange,
  itemLabelKey,
  itemIdKey,
  itemExtraLabelKey,
  itemTooltipKey,
}) {
  const classes = useStyles();

  const isMobile = IsMobile();

  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [leftFilterValue, setLeftFilterValue] = useState("");
  const [rightFilterValue, setRightFilterValue] = useState("");

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const numberOfChecked = useCallback(
    (items) => intersection(checked, items).length,
    [checked]
  );

  const handleToggleAll = useCallback(
    (items) => () => {
      if (numberOfChecked(items) === items.length) {
        setChecked(not(checked, items));
      } else {
        setChecked(union(checked, items));
      }
    },
    [checked, numberOfChecked]
  );

  const handleAllRight = () => {
    setChecked([]);
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setChecked([]);
    setLeft(left.concat(right));
    setRight([]);
  };

  const onDrop = useCallback(
    (event, category) => {
      const dropItem = JSON.parse(event.dataTransfer.getData("item"));
      const dropItemIsChecked = checked.some(
        (item) => item[itemIdKey] === dropItem[itemIdKey]
      );

      if (category === "left") {
        if (left.some((item) => item[itemIdKey] === dropItem[itemIdKey])) {
          return;
        } else {
          const cleanRight = right
            .filter((item) => item[itemIdKey] !== dropItem[itemIdKey])
            .filter(
              (item) =>
                !checked.some(
                  (checkedItem) => checkedItem[itemIdKey] === item[itemIdKey]
                )
            );

          const nonUsedItems = checked.filter(
            (item) =>
              !left.some((usedItem) => usedItem[itemIdKey] === item[itemIdKey])
          );

          if (
            !dropItemIsChecked &&
            !left.some((item) => item[itemIdKey] === dropItem[itemIdKey])
          ) {
            nonUsedItems.push(dropItem);
          }

          setLeft([...left, ...nonUsedItems]);
          setRight(cleanRight);

          if (checked.length) {
            setChecked([]);
          }
        }
      } else {
        if (right.some((item) => item[itemIdKey] === dropItem[itemIdKey])) {
          return;
        } else {
          const cleanLeft = left
            .filter((item) => item[itemIdKey] !== dropItem[itemIdKey])
            .filter(
              (item) =>
                !checked.some(
                  (checkedItem) => checkedItem[itemIdKey] === item[itemIdKey]
                )
            );

          const nonUsedItems = checked.filter(
            (item) =>
              !right.some((usedItem) => usedItem[itemIdKey] === item[itemIdKey])
          );

          if (
            !dropItemIsChecked &&
            !right.some((item) => item[itemIdKey] === dropItem[itemIdKey])
          ) {
            nonUsedItems.push(dropItem);
          }

          setRight([...right, ...nonUsedItems]);
          setLeft(cleanLeft);

          if (checked.length) {
            setChecked([]);
          }
        }
      }
    },
    [checked, itemIdKey, left, right]
  );

  useEffect(() => {
    setLeft(initialLeft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLeft]);

  useEffect(() => {
    setRight(initialRight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRight]);

  useEffect(() => {
    if (onLeftChange) {
      onLeftChange(left);
    }
  }, [left, onLeftChange]);

  useEffect(() => {
    if (onRightChange) {
      onRightChange(right);
    }
  }, [onRightChange, right]);

  return (
    <Grid container alignItems="flex-start" className={classes.root}>
      <Grid item xs={isMobile ? 10 : 5}>
        <ItemsList
          label={leftLabel}
          initialItems={left}
          side="left"
          filterValue={leftFilterValue}
          onFilterValueChange={setLeftFilterValue}
          loading={loading}
          onDrop={onDrop}
          handleToggleAll={handleToggleAll}
          itemLabelKey={itemLabelKey}
          itemExtraLabelKey={itemExtraLabelKey}
          itemTooltipKey={itemTooltipKey}
          checked={checked}
          setChecked={setChecked}
          numberOfChecked={numberOfChecked}
        />
      </Grid>
      <Grid item xs={isMobile ? 2 : "auto"} style={{ alignSelf: "center" }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleAllRight}
            disabled={loading || !left || left.length === 0}
            aria-label="mover todos para a direita"
          >
            ≫
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={loading || leftChecked.length === 0}
            aria-label="mover selecionado para a direita"
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={loading || rightChecked.length === 0}
            aria-label="mover selecionado para a esquerda"
          >
            &lt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleAllLeft}
            disabled={loading || !right || right.length === 0}
            aria-label="mover todos para a esquerda"
          >
            ≪
          </Button>
        </Box>
      </Grid>
      <Grid
        item
        xs={isMobile ? 12 : 5}
        style={
          isMobile
            ? {
                borderTop: "1px solid #C8C8C8",
                marginTop: "1.5rem",
                paddingTop: "1rem",
              }
            : {}
        }
      >
        <ItemsList
          label={rightLabel}
          initialItems={right}
          side="right"
          filterValue={rightFilterValue}
          onFilterValueChange={setRightFilterValue}
          loading={loading}
          onDrop={onDrop}
          handleToggleAll={handleToggleAll}
          itemLabelKey={itemLabelKey}
          itemExtraLabelKey={itemExtraLabelKey}
          itemTooltipKey={itemTooltipKey}
          checked={checked}
          setChecked={setChecked}
          numberOfChecked={numberOfChecked}
        />
      </Grid>
    </Grid>
  );
}
